// src/amBrief/index.js  →  dist/amBrief.js  (global: AmBrief)
//
// The Area-Manager Brief in the MANAGERS app. Leads with the AI BRIEF — a written
// company summary + a ranked "priority visits" list synthesised from sales, SVRs,
// customer reviews, Top 50, add-ons and performance reviews (regenerated after the
// daily sales upload; Head Office can regenerate on demand). Below it, the hard
// sales numbers (month-to-date vs target). Auth = the admin Bearer (DB.getToken).

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
export function tone(pct) { return pct == null ? '' : pct < 75 ? 'red' : pct < 90 ? 'amber' : 'green'; }
export function toneColor(t) { return t === 'red' ? '#DC2626' : t === 'amber' ? '#D97706' : t === 'green' ? '#059669' : '#94a3b8'; }
export function fmtAED(n) { return 'AED ' + String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
export function sevColor(s) { return s === 'high' ? '#DC2626' : s === 'low' ? '#6b7280' : '#D97706'; }

function api(path, opts) {
  opts = opts || {};
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (opts.body) headers['Content-Type'] = 'application/json';
  return fetch(base + path, { cache: 'no-store', method: opts.method || 'GET', headers: headers, body: opts.body });
}
function root() { return document.getElementById('amBriefRoot'); }
// Head Office only generates/regenerates the brief (the Worker enforces this; the
// button is hidden for area managers, who read it). Reads the app's currentAdmin
// global, falling back to the stored session — both carry role 'admin' for HO.
function isHeadOffice() {
  try { if (typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.role) return String(currentAdmin.role).toLowerCase() === 'admin'; } catch (e) { /* not in scope */ }
  try { const u = JSON.parse((typeof sessionStorage !== 'undefined' && sessionStorage.getItem('elcAdminUser')) || 'null'); return !!(u && String(u.role).toLowerCase() === 'admin'); } catch (e) { /* ignore */ }
  return false;
}
function bar(pct, t) {
  const w = Math.max(0, Math.min(100, Number(pct) || 0));
  return '<div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden;margin-top:4px">' +
    '<div style="height:100%;width:' + w + '%;background:' + toneColor(t) + '"></div></div>';
}
function fmtWhen(iso) {
  const t = Date.parse(iso);
  if (isNaN(t)) return '';
  try { return new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return String(iso).slice(0, 16).replace('T', ' '); }
}

export async function render() {
  const el = root();
  if (!el) return;
  el.innerHTML = '<div style="padding:20px;color:var(--tiny)">Loading your brief…</div>';
  let ai = null, sales = null;
  try {
    const [aiRes, salesRes] = await Promise.all([api('/api/admin/am-brief-ai'), api('/api/admin/morning-brief')]);
    if (aiRes.status === 401 || aiRes.status === 403) { el.innerHTML = '<div style="padding:20px;color:var(--adm-red-text)">Your session looks expired — sign out and back in.</div>'; return; }
    if (aiRes.ok) ai = (await aiRes.json()).brief;
    if (salesRes.ok) sales = await salesRes.json();
  } catch (e) {
    el.innerHTML = '<div style="padding:20px;color:var(--adm-red-text)">Could not load the brief (' + esc((e && e.message) || e) + ').</div>';
    return;
  }
  el.innerHTML = '';
  const aiDiv = document.createElement('div'); el.appendChild(aiDiv); drawAiBrief(aiDiv, ai);
  const salesDiv = document.createElement('div'); el.appendChild(salesDiv); if (sales) drawSales(salesDiv, sales);
}

// HEAD OFFICE regenerates the brief (the Worker enforces HO-only; AMs get a clear
// message). The call hits the model, so it takes a few seconds — show a spinner.
export async function generate(btn) {
  const el = root(); if (!el) return;
  const label = isHeadOffice() ? 'Generate now' : 'Generate your area';   // company vs your-area scope
  if (btn) { btn.disabled = true; btn.textContent = 'Generating… (a few seconds)'; }
  try {
    const res = await api('/api/admin/am-brief-ai/generate', { method: 'POST', body: '{}' });
    if (res.status === 403) { if (typeof alert === 'function') alert('You do not have permission to generate the brief.'); if (btn) { btn.disabled = false; btn.textContent = label; } return; }
    if (!res.ok) throw new Error('status ' + res.status);
    await render();   // reload with the fresh brief (HO: company; area manager: their area)
  } catch (e) {
    if (typeof alert === 'function') alert('Could not generate the brief (' + ((e && e.message) || e) + ').');
    if (btn) { btn.disabled = false; btn.textContent = label; }
  }
}

function genBtn(label) {
  return '<button onclick="AmBrief.generate(this)" style="margin-top:10px;padding:9px 16px;border:1.5px solid var(--green,#009E3D);background:var(--card);color:var(--green,#009E3D);border-radius:10px;font-weight:700;font-size:13px;cursor:pointer;font-family:inherit">' + esc(label) + '</button>';
}

function drawAiBrief(el, brief) {
  if (!brief || (!brief.summary && !(brief.priorities && brief.priorities.length))) {
    el.innerHTML = '<div style="margin-bottom:8px"><div style="font-size:18px;font-weight:800">☀️ AI Brief' +
      (isHeadOffice() ? '' : ' <span style="font-size:12px;color:var(--sub);font-weight:600">(your area)</span>') + '</div>' +
      '<div style="font-size:12px;color:var(--tiny)">A daily AI read of every store — sales, visits, reviews, Top 50, add-ons and performance — with the stores that most need a visit.</div></div>' +
      '<div style="padding:18px;border:1px dashed var(--border-strong);border-radius:12px;color:var(--tiny)">No brief yet. It generates automatically after the daily sales upload — or generate one now.' +
      '<div>' + genBtn(isHeadOffice() ? 'Generate now' : 'Generate your area') + '</div></div>';
    return;
  }
  const priorities = Array.isArray(brief.priorities) ? brief.priorities : [];
  const head = '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:8px">' +
    '<div><div style="font-size:18px;font-weight:800">☀️ AI Brief' +
    (isHeadOffice() ? '' : ' <span style="font-size:12px;color:var(--sub);font-weight:600">(your area)</span>') + '</div>' +
    '<div style="font-size:11px;color:var(--tiny)">Generated ' + esc(fmtWhen(brief.generated_at)) + (brief.generated_by ? ' · ' + esc(brief.generated_by) : '') + '</div></div>' +
    '<button onclick="AmBrief.generate(this)" title="Regenerate" style="border:1px solid var(--border);background:var(--card);border-radius:8px;padding:6px 10px;font-size:12px;cursor:pointer;font-family:inherit;color:var(--tiny);white-space:nowrap">↻ ' + (isHeadOffice() ? 'Regenerate' : 'Regenerate (your area)') + '</button></div>';
  const summary = brief.summary
    ? '<div translate="no" style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px;font-size:14px;line-height:1.55;white-space:pre-wrap">' + esc(brief.summary) + '</div>'
    : '';
  const prio = priorities.length
    ? '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:16px 0 8px">Priority visits</div>' +
      priorities.map(function (p) {
        return '<div style="border-inline-start:3px solid ' + sevColor(p.severity) + ';padding:6px 0 6px 10px;margin-bottom:8px">' +
          '<div style="font-weight:700"><span translate="no">' + esc(p.store) + '</span>' +
          ' <span style="font-size:11px;font-weight:700;color:' + sevColor(p.severity) + '">' + esc((p.severity || 'medium').toUpperCase()) + '</span></div>' +
          '<div translate="no" style="font-size:13px;color:var(--tiny)">' + esc(p.reason) + '</div></div>';
      }).join('')
    : '<div style="margin:14px 0;color:var(--adm-ok-text);font-size:14px">No stores flagged for a priority visit. 🎉</div>';
  el.innerHTML = head + summary + prio;
}

// The supporting sales numbers (month-to-date vs target) — the old static brief.
function drawSales(el, d) {
  const c = d.company || { actual: 0, target: 0, pct: null };
  const t = tone(c.pct);
  const problems = Array.isArray(d.problem_stores) ? d.problem_stores : [];
  const leaders = Array.isArray(d.leaders) ? d.leaders : [];
  const areas = Array.isArray(d.areas) ? d.areas : [];

  const head = '<div style="margin:24px 0 12px;border-top:1px solid var(--border);padding-top:18px"><div style="font-size:15px;font-weight:800">Sales detail' +
    (d.scoped ? ' <span style="font-size:12px;color:var(--sub);font-weight:600">(your area)</span>' : ' <span style="font-size:12px;color:var(--sub);font-weight:600">(company)</span>') +
    '</div><div style="font-size:12px;color:var(--tiny)">' + (d.period && d.period.closed ? 'Last month, final, vs target. ' : 'Month-to-date vs target. ') + (d.period && d.period.from ? esc(d.period.from) + ' → ' + esc(d.period.to) : '') + '</div></div>';

  const storeCount = d.store_count || 0;
  if (d.sales_unavailable) {   // the sales read failed or nothing is in yet: say so, never "every store is on target"
    el.innerHTML = head + '<div style="padding:16px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">Sales figures are not available just now. Try again shortly.</div>';
    return;
  }
  if (!storeCount || c.target === 0) {
    el.innerHTML = head + '<div style="padding:16px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">No sales/target data for this period yet.</div>';
    return;
  }

  const grade = c.pct == null ? 'no target' : c.pct >= 100 ? 'ahead of target' : c.pct >= 90 ? 'on track' : c.pct >= 75 ? 'behind target' : 'well behind';
  const tile = '<div style="display:flex;gap:14px;align-items:center;background:var(--card);border:1px solid var(--border);border-radius:14px;padding:16px;margin-bottom:16px">' +
    '<div style="font-size:40px;font-weight:900;line-height:1;color:' + toneColor(t) + '">' + (c.pct == null ? '—' : c.pct + '%') + '</div>' +
    '<div><div style="font-weight:700">' + esc(grade) + '</div>' +
    '<div style="font-size:13px;color:var(--tiny)">' + fmtAED(c.actual) + ' of ' + fmtAED(c.target) + ' · ' + storeCount + ' store' + (storeCount === 1 ? '' : 's') + '</div></div></div>';

  const areaHtml = (areas.length > 1)
    ? '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:6px 0 8px">By area</div>' +
      areas.map(function (a) {
        const at = tone(a.pct);
        return '<div style="border:1px solid var(--border);border-radius:10px;padding:10px 12px;margin-bottom:8px;background:var(--card)">' +
          '<div style="display:flex;justify-content:space-between"><span style="font-weight:700"><span translate="no">' + esc(a.area) + '</span>' + ' <span style="opacity:.6;font-weight:400">(' + a.store_count + ')</span></span>' +
          '<span style="font-weight:700;color:' + toneColor(at) + '">' + (a.pct == null ? '—' : a.pct + '%') + '</span></div>' + bar(a.pct, at) + '</div>';
      }).join('')
    : '';

  const problemHtml = problems.length
    ? '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--adm-red-text);margin:16px 0 8px">Behind target</div>' +
      problems.slice(0, 12).map(function (s) {
        return '<div style="display:flex;justify-content:space-between;gap:8px;border-inline-start:3px solid ' + toneColor(tone(s.pct)) + ';padding:6px 0 6px 10px;margin-bottom:6px">' +
          '<span><b translate="no">' + esc(s.store) + '</b>' + (s.area ? ' <span style="opacity:.6;font-size:12px">(' + esc(s.area) + ')</span>' : '') + '</span>' +
          '<span style="white-space:nowrap"><b style="color:' + toneColor(tone(s.pct)) + '">' + s.pct + '%</b> · ' + fmtAED(s.actual) + '</span></div>';
      }).join('')
    : '<div style="margin:16px 0;color:var(--adm-ok-text);font-size:14px">Every store is on or ahead of target. 🎉</div>';

  const leaderHtml = leaders.length
    ? '<div style="font-size:12px;color:var(--tiny);margin-top:14px">Leading: ' +
        leaders.slice(0, 3).map(function (l) { return '<b translate="no">' + esc(l.store) + '</b> ' + l.pct + '%'; }).join(' · ') + '</div>'
    : '';

  el.innerHTML = head + tile + areaHtml + problemHtml + leaderHtml;
}
